
import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRange } from "react-day-picker";
import { DateRangeFilterPicker } from "@/components/DateRangeFilterPicker";
import { Download, Search } from "lucide-react";
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { format } from 'date-fns';
import { monthlyRevenueData, generateBusinessPlanSubscriptions, BusinessPlanSubscription } from '@/components/saas/landing/business-opportunity/revenue-scenarios';
import { subscriptionPlans, SubscriptionPlan } from '@/types/subscriptionPlans';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const BusinessPlanSalesReport: React.FC = () => {
  // State variables for filtering and data
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [businessPlans, setBusinessPlans] = useState<SubscriptionPlan[]>(subscriptionPlans);
  
  // Load business plans from localStorage (same as business signup page)
  useEffect(() => {
    const savedPlans = localStorage.getItem('business_signup_plans');
    if (savedPlans) {
      try {
        const parsedPlans = JSON.parse(savedPlans);
        setBusinessPlans(parsedPlans);
        console.log("Loaded business plans from localStorage:", parsedPlans);
      } catch (error) {
        console.error('Error parsing saved business signup plans:', error);
      }
    }
  }, []);
  
  // Generate sample data based on the actual business plans
  const subscriptions = useMemo(() => {
    // Map the business plans to the correct structure for the sample data
    return generateBusinessPlanSubscriptions(100).map(sub => {
      // Find the matching plan in the business plans
      const planIndex = parseInt(sub.planId.replace('scenario-', '')) - 1;
      const matchingPlan = businessPlans[Math.min(planIndex, businessPlans.length - 1)];
      
      if (matchingPlan) {
        return {
          ...sub,
          planId: matchingPlan.id,
          planName: matchingPlan.name,
          price: matchingPlan.monthlyPrice
        };
      }
      return sub;
    });
  }, [businessPlans]);
  
  // Filter subscriptions based on search, date range, status, and plan
  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter(subscription => {
      // Filter by search term
      const searchMatch = 
        subscription.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subscription.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subscription.planName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (subscription.licenseKey && subscription.licenseKey.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Filter by date range
      const dateMatch = !dateRange?.from || (
        subscription.startDate >= dateRange.from &&
        (!dateRange.to || subscription.startDate <= dateRange.to)
      );
      
      // Filter by status
      const statusMatch = statusFilter === 'all' || subscription.status === statusFilter;
      
      // Filter by plan
      const planMatch = planFilter === 'all' || subscription.planId === planFilter;
      
      return searchMatch && dateMatch && statusMatch && planMatch;
    });
  }, [subscriptions, searchTerm, dateRange, statusFilter, planFilter]);
  
  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const total = filteredSubscriptions.length;
    const active = filteredSubscriptions.filter(s => s.status === 'active').length;
    const trial = filteredSubscriptions.filter(s => s.status === 'trial').length;
    const cancelled = filteredSubscriptions.filter(s => s.status === 'cancelled').length;
    
    const totalRevenue = filteredSubscriptions
      .filter(s => s.status === 'active')
      .reduce((sum, s) => sum + s.price, 0);
    
    // Calculate plan distribution for pie chart based on the actual business plans
    const planCounts = businessPlans.map(plan => {
      const count = filteredSubscriptions.filter(s => s.planId === plan.id).length;
      return {
        name: plan.name,
        value: count,
        price: plan.monthlyPrice
      };
    });
    
    return {
      total,
      active,
      trial,
      cancelled,
      totalRevenue,
      planCounts
    };
  }, [filteredSubscriptions, businessPlans]);
  
  // Handle export to CSV
  const handleExportCSV = () => {
    const headers = [
      'ID', 'Plan', 'Customer', 'Email', 'Price', 'Status', 
      'Billing Cycle', 'Start Date', 'Next Billing', 'License Key', 'Super Admin'
    ].join(',');
    
    const rows = filteredSubscriptions.map(subscription => [
      subscription.id,
      subscription.planName,
      subscription.customerName,
      subscription.customerEmail,
      subscription.price,
      subscription.status,
      subscription.billingCycle,
      format(subscription.startDate, 'yyyy-MM-dd'),
      subscription.nextBillingDate ? format(subscription.nextBillingDate, 'yyyy-MM-dd') : '',
      subscription.licenseKey || '',
      subscription.superAdminName || ''
    ].join(','));
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'business-plan-sales.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Active Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summaryStats.active}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Trial Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{summaryStats.trial}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Monthly Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{formatCurrency(summaryStats.totalRevenue)}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Plan Distribution Pie Chart */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Plan Distribution</CardTitle>
            <CardDescription>Breakdown of active subscriptions by plan</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={summaryStats.planCounts}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {summaryStats.planCounts.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value} subscriptions`, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        {/* Monthly Revenue Bar Chart */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Monthly Revenue</CardTitle>
            <CardDescription>Business plan revenue over the past year</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `$${value / 1000}k`} />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {businessPlans.map((plan, index) => (
          <Card key={plan.id} className={plan.recommended ? "border-green-500" : ""}>
            <CardHeader className={`pb-2 ${plan.recommended ? "bg-green-50" : ""}`}>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                {plan.recommended && <Badge className="bg-green-500">Popular</Badge>}
              </div>
              <CardDescription>${plan.monthlyPrice}/month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Active:</span>
                  <span className="font-medium">{
                    filteredSubscriptions.filter(
                      s => s.planId === plan.id && s.status === 'active'
                    ).length
                  }</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Trial:</span>
                  <span className="font-medium">{
                    filteredSubscriptions.filter(
                      s => s.planId === plan.id && s.status === 'trial'
                    ).length
                  }</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Revenue:</span>
                  <span className="font-medium">{
                    formatCurrency(
                      filteredSubscriptions
                        .filter(s => s.planId === plan.id && s.status === 'active')
                        .reduce((sum, s) => sum + s.price, 0)
                    )
                  }</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Filters and Table */}
      <Card>
        <CardHeader>
          <CardTitle>Business Plan Subscriptions</CardTitle>
          <CardDescription>Detailed list of all business plan subscriptions</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter Section */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-grow">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, plan, or license..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {businessPlans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <DateRangeFilterPicker
              dateRange={dateRange}
              onChange={(range) => setDateRange(range)}
            />
            
            <Button variant="outline" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
          
          {/* Subscriptions Table */}
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>License Key</TableHead>
                  <TableHead>Super Admin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscriptions.slice(0, 10).map((subscription) => (
                  <TableRow key={subscription.id}>
                    <TableCell className="font-medium">{subscription.planName}</TableCell>
                    <TableCell>
                      <div>{subscription.customerName}</div>
                      <div className="text-xs text-gray-500">{subscription.customerEmail}</div>
                    </TableCell>
                    <TableCell>{formatCurrency(subscription.price)}/{subscription.billingCycle.slice(0, 2)}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          subscription.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' :
                          subscription.status === 'trial' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          subscription.status === 'cancelled' ? 'bg-gray-100 text-gray-800 border-gray-200' :
                          'bg-red-100 text-red-800 border-red-200'
                        }
                      >
                        {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>{format(subscription.startDate, 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      {subscription.licenseKey ? (
                        <div className="max-w-[150px] truncate text-xs font-mono">
                          {subscription.licenseKey}
                        </div>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {subscription.superAdminName || <span className="text-gray-400">None</span>}
                    </TableCell>
                  </TableRow>
                ))}
                
                {filteredSubscriptions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No subscriptions found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {filteredSubscriptions.length > 10 && (
            <div className="flex justify-center mt-4 text-sm text-gray-500">
              Showing 10 of {filteredSubscriptions.length} subscriptions
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessPlanSalesReport;
